import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 1000;

export default function GroupDisplay() {
  const { groupId } = useParams();
  const [screens, setScreens] = useState([]);
  const [groupConfig, setGroupConfig] = useState(null);
  const [menuContent, setMenuContent] = useState({}); // {screen_id: content}
  const [combinedAd, setCombinedAd] = useState(null);
  const [showLogo, setShowLogo] = useState(true);
  const videoRef = useRef(null);
  const previousAdIdRef = useRef(null);

  // === NOWE: Cache do przechowywania ostatniego poprawnego stanu ===
  const lastValidStateRef = useRef(null);
  const failedAttemptsRef = useRef(0);
  const MAX_FAILED_ATTEMPTS = 10; // Po 10 nieudanych próbach (10 sekund) pokaż logo
  
  // === NOWE: Ref do śledzenia poprzedniej treści dla cross-fade ===
  const [previousCombinedAd, setPreviousCombinedAd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Poll for content updates using new grid endpoint
  const fetchContent = useCallback(async () => {
    if (!groupId) return;

    try {
      const response = await axios.get(`${API_URL}/display/group/${groupId}/grid`, {
        timeout: 5000 // 5 sekund timeout
      });
      const { group, menu_content, combined_ad, screens: updatedScreens } = response.data;
      
      // === Reset licznika błędów po udanym połączeniu ===
      if (failedAttemptsRef.current > 0) {
        console.log('✅ Połączenie przywrócone po', failedAttemptsRef.current, 'nieudanych próbach');
      }
      failedAttemptsRef.current = 0;
      
      // Update group config
      if (group) {
        setGroupConfig(group);
      }
      
      // Update screens if we got them
      if (updatedScreens && updatedScreens.length > 0) {
        setScreens(updatedScreens);
      }
      
      // menu_content is a dict: {screen_id: {screen, content}}
      const newMenuContent = {};
      Object.keys(menu_content).forEach(screenId => {
        newMenuContent[screenId] = menu_content[screenId].content;
      });
      
      // === Cross-fade: zapisz poprzednią reklamę przed aktualizacją ===
      setCombinedAd(prevAd => {
        if (prevAd && combined_ad && prevAd.id !== combined_ad.id) {
          // Zmiana reklamy - uruchom cross-fade
          setPreviousCombinedAd(prevAd);
          setIsTransitioning(true);
          setTimeout(() => {
            setIsTransitioning(false);
            setPreviousCombinedAd(null);
          }, 500); // 500ms transition
        } else if (prevAd && !combined_ad) {
          // Reklama się skończyła - fade out
          setPreviousCombinedAd(prevAd);
          setIsTransitioning(true);
          setTimeout(() => {
            setIsTransitioning(false);
            setPreviousCombinedAd(null);
          }, 500);
        }
        return combined_ad;
      });
      
      setMenuContent(newMenuContent);
      
      // === Zapisz ostatni poprawny stan do cache ===
      lastValidStateRef.current = {
        groupConfig: group,
        screens: updatedScreens,
        menuContent: newMenuContent,
        combinedAd: combined_ad,
        timestamp: Date.now()
      };
      
      setShowLogo(Object.keys(newMenuContent).length === 0 && !combined_ad);
      
    } catch (error) {
      failedAttemptsRef.current += 1;
      const errorType = error.code === 'ECONNABORTED' ? 'timeout' : 
                        error.message.includes('Network') ? 'network' : 'unknown';
      
      console.warn(`⚠️ Błąd połączenia [${errorType}] (próba ${failedAttemptsRef.current}/${MAX_FAILED_ATTEMPTS}):`, error.message);
      
      // === Jeśli mamy cache i nie przekroczyliśmy limitu - zachowaj ostatni stan ===
      if (lastValidStateRef.current && failedAttemptsRef.current < MAX_FAILED_ATTEMPTS) {
        console.log(`🔄 Używam cache z ${new Date(lastValidStateRef.current.timestamp).toLocaleTimeString()} - ekran pozostaje bez zmian`);
        // Nie zmieniaj nic - zachowaj aktualny stan wyświetlania
      } else if (failedAttemptsRef.current >= MAX_FAILED_ATTEMPTS) {
        console.error('❌ Przekroczono limit prób połączenia, pokazuję logo. Sprawdź połączenie sieciowe.');
        setShowLogo(true);
      }
    }
  }, [groupId]);

  useEffect(() => {
    fetchContent();
    const interval = setInterval(fetchContent, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchContent]);

  // Force video play when combinedAd changes (for Chrome/Firefox)
  useEffect(() => {
    // Check if it's a video - can be type 'video' OR 'ad_combined' with .mp4/.webm extension
    const isVideo = combinedAd && (
      combinedAd.type === 'video' || 
      (combinedAd.type === 'ad_combined' && combinedAd.file_url && 
       (combinedAd.file_url.endsWith('.mp4') || combinedAd.file_url.endsWith('.webm')))
    );
    
    if (isVideo) {
      // Check if this is actually a NEW video (different ID)
      const currentAdId = combinedAd.id;
      const isNewVideo = previousAdIdRef.current !== currentAdId;
      
      if (isNewVideo) {
        console.log('🎥 NEW combined video detected:', combinedAd.title);
        previousAdIdRef.current = currentAdId;
        
        // Wait a tick for ref to be assigned
        setTimeout(() => {
          if (videoRef.current) {
            const video = videoRef.current;
            console.log('📹 Video element ready, starting playback');
            
            video.muted = true;
            // DON'T call video.load() - it causes restart!
            
            const playVideo = () => {
              console.log('▶️ Attempting to play video...');
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    console.log('✅ Combined video playing successfully!');
                  })
                  .catch(err => {
                    console.error('❌ Combined video autoplay failed:', err);
                    // Fallback: try on next user interaction
                    const clickHandler = () => {
                      console.log('🖱️ User clicked, retrying video play...');
                      video.play().catch(e => console.error('Manual play failed:', e));
                    };
                    document.addEventListener('click', clickHandler, { once: true });
                  });
              }
            };
            
            playVideo();
          }
        }, 100);
      } else {
        // Same video, just ignore (polling is fetching same data)
        // console.log('⏭️ Same video, skipping restart');
      }
    } else if (!combinedAd) {
      // Video removed, reset tracking
      previousAdIdRef.current = null;
    }
  }, [combinedAd]);

  // === Helper: sprawdź czy content to wideo ===
  const isVideoContent = (content) => {
    if (!content) return false;
    return content.type === 'video' || 
      (content.type === 'ad_combined' && content.file_url && 
       (content.file_url.endsWith('.mp4') || content.file_url.endsWith('.webm')));
  };

  // === Render pojedynczego elementu content (image lub video) ===
  const renderContentElement = (content, isOverlay = false) => {
    if (!content) return null;
    
    if (isVideoContent(content)) {
      return (
        <video
          key={content.id}
          ref={isOverlay ? videoRef : null}
          src={BACKEND_URL + content.file_url}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          style={{ pointerEvents: 'none' }}
          onError={(e) => console.error('Video error:', e.target.error)}
          onLoadedData={() => console.log('Video loaded:', content.title)}
        />
      );
    }
    
    return (
      <img
        key={content.id}
        src={BACKEND_URL + content.file_url}
        alt={content.title || 'Content'}
        className="w-full h-full object-cover"
      />
    );
  };

  const renderScreenContent = (screen) => {
    const content = menuContent[screen.id];
    
    if (!content) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-zinc-950">
          <img 
            src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
            alt="AvePark" 
            className="w-1/2"
          />
        </div>
      );
    }

    if (content.type === 'video') {
      return (
        <video
          key={content.id}
          src={BACKEND_URL + content.file_url}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          style={{ pointerEvents: 'none' }}
          ref={(video) => {
            if (video) {
              video.muted = true;
              video.setAttribute('playsinline', 'true');
              video.setAttribute('webkit-playsinline', 'true');
              video.controls = false;
              video.removeAttribute('controls');
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(err => console.error('Video autoplay failed:', err));
              }
            }
          }}
        />
      );
    } else if (content.type === 'menu' && content.editor_data) {
      return (
        <img 
          key={content.id}
          src={content.editor_data.preview} 
          alt="Menu"
          className="w-full h-full object-contain"
        />
      );
    } else if (content.file_url) {
      return (
        <img
          key={content.id}
          src={BACKEND_URL + content.file_url}
          alt={content.title}
          className="w-full h-full object-cover"
        />
      );
    }

    return null;
  };

  if (showLogo) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
          alt="AvePark" 
          className="w-1/2 max-w-lg"
        />
      </div>
    );
  }

  // Generate grid CSS based on layout config
  const getGridStyle = () => {
    if (!groupConfig || !groupConfig.layout_config) {
      return { gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: '1fr' };
    }

    const { rows, cols } = groupConfig.layout_config;
    return {
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gridTemplateRows: `repeat(${rows}, 1fr)`
    };
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      {/* BACKGROUND: Dynamic grid showing menus */}
      <div className="absolute inset-0 grid bg-black" style={getGridStyle()}>
        {screens.map((screen) => (
          <div 
            key={screen.id}
            className="w-full h-full"
          >
            {renderScreenContent(screen)}
          </div>
        ))}
      </div>

      {/* === NOWE: Cross-fade overlay system === */}
      {/* Warstwa 1: Poprzednia reklama (fade out) */}
      {previousCombinedAd && isTransitioning && (
        <div 
          className="absolute inset-0 z-40 bg-black transition-opacity duration-500 opacity-0"
        >
          {renderContentElement(previousCombinedAd, false)}
        </div>
      )}

      {/* Warstwa 2: Aktualna reklama (zawsze widoczna gdy aktywna) */}
      {combinedAd && (
        <div 
          className={`absolute inset-0 z-50 bg-black transition-opacity duration-500 ${isTransitioning ? 'opacity-100' : 'opacity-100'}`}
        >
          {isVideoContent(combinedAd) ? (
            <video
              ref={videoRef}
              key={combinedAd.id}
              src={BACKEND_URL + combinedAd.file_url}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls={false}
              disablePictureInPicture
              disableRemotePlayback
              style={{ pointerEvents: 'none' }}
              onError={(e) => console.error('Video error:', e.target.error)}
              onLoadedData={() => console.log('Video loaded successfully')}
            />
          ) : (
            <img
              key={combinedAd.id}
              src={BACKEND_URL + combinedAd.file_url}
              alt={combinedAd.title}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      {/* === NOWE: Rozbudowane style CSS dla ukrycia kontrolek video === */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        /* Ukryj natywne kontrolki video na wszystkich przeglądarkach */
        video::-webkit-media-controls {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        video::-webkit-media-controls-enclosure {
          display: none !important;
        }
        video::-webkit-media-controls-panel {
          display: none !important;
        }
        video::-webkit-media-controls-play-button {
          display: none !important;
        }
        video::-webkit-media-controls-start-playback-button {
          display: none !important;
        }
        video::-webkit-media-controls-overlay-play-button {
          display: none !important;
        }
        video::-moz-media-controls {
          display: none !important;
        }
        video::media-controls {
          display: none !important;
        }
        
        /* Zapobiegaj interakcji z video */
        video {
          pointer-events: none !important;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
          user-select: none;
        }
        
        /* Ukryj wszystkie overlay elementy */
        video::-webkit-media-controls-overlay-enclosure {
          display: none !important;
        }
      `}</style>
    </div>
  );
}